import { useEffect, useState, useRef } from 'react';
import { NextSeo } from 'next-seo';
import { AppRoutes } from 'src/lib/routes';
import AccordionFaq from 'src/components/AccordionFaq';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import Slider from 'react-slick';
import { commonCarouselSettings, HEADER_HEIGHT } from 'src/lib/constants';
import HeroImage from 'public/images/creator_community/hero.jpg';
import Banner1Image from 'public/images/creator_community/img_1.jpg';
import Banner2Image from 'public/images/creator_community/img_2.jpg';
import Banner3Image from 'public/images/creator_community/img_3.jpg';
import Icon1 from 'public/images/creator_community/icon_1.png';
import Icon2 from 'public/images/creator_community/icon_2.png';
import Icon3 from 'public/images/creator_community/icon_3.png';
import imgInstagram1 from 'public/images/creator_community/ig_1.jpg';
import imgInstagram2 from 'public/images/creator_community/ig_2.jpg';
import imgInstagram3 from 'public/images/creator_community/ig_3.jpg';
import imgInstagram4 from 'public/images/creator_community/ig_4.jpg';
import imgInstagram5 from 'public/images/creator_community/ig_5.jpg';
import imgInstagram6 from 'public/images/creator_community/ig_6.jpg';
import noVideoImg from 'public/images/creator_community/no_video.jpeg';
import styles from 'src/styles/modules/pages/brand.module.scss';
import videoCardStyle from 'src/styles/modules/components/video_card.module.scss';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Link from 'next/link';
import VideoCard from 'src/components/VideoCard';
import IlluImage from 'public/images/illu/illu_16.png';
import ClientOnlyComponent from 'src/components/ClientOnlyComponent';
import CardShuffle from 'src/components/CardShuffle';
import Player from 'src/lib/video_player';
import { getEleOffset } from 'src/helpers';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import { GetStaticProps } from 'next';
import { Translation } from 'src/types';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const registrationUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSf7_J6FZ660Y9pglBWnVtZUDH8ztRqc7rlOWiaq_IuhJiLhrw/viewform'
const faqs = (translation: Translation) => ([
  {
    question: translation('whatWillIBeDoing'),
    answer: translation('otherThanHaving'),
  },
  {
    question: translation('whatIsCovered'),
    answer: translation('theExperience'),
  },
  {
    question: translation('doCreatorsNeed'),
    answer: translation('youCanBe'),
  },
  {
    question: translation('howWillYouChoose'),
    answer: translation('creatorsWhoseStyle'),
  },
  {
    question: translation('doIGetToSelect'),
    answer: translation('weWillLetYouKnow'),
  },
  {
    question: translation('howManyTrips'),
    answer: translation('theseIsNoMinimum'),
  },
  {
    question: translation('whatAreTheDeliverables'),
    answer: (
      <>
        {translation('deliverables')}
        <div className="tw-mb-3">
          <div>{translation('theMinimumRequirement')}</div>
          <div>
            {translation('theMinimumRequirementForVideo')}
          </div>
        </div>
        <div>
          {translation('ifWeAreAble')}
        </div>
      </>
    ),
  },
  {
    question: translation('whereWillMyContent'),
    answer: translation('yourContentWillBeUsed'),
  },
  {
    question: translation('willThereBe'),
    answer: translation('noAllowance'),
  },
  {
    question: translation('isThereAnAgeLimit'),
    answer: translation('applicantsMustBeAt'),
  },
  {
    question: translation('willIBeRequired'),
    answer: translation('theDefaultArrangement'),
  },
]);

const igImages = [
  {
    src: imgInstagram1,
    altTranslationKey: "altInstagram1",
    url: 'https://www.instagram.com/marcus_cchow/',
  },
  {
    src: imgInstagram2,
    altTranslationKey: "altInstagram2",
    url: 'https://www.instagram.com/akifkeith/',
  },
  {
    src: imgInstagram3,
    altTranslationKey: "altInstagram3",
    url: 'https://www.instagram.com/eshton/',
  },
  {
    src: imgInstagram4,
    altTranslationKey: "altInstagram4",
    url: 'https://www.instagram.com/gottagoh/',
  },
  {
    src: imgInstagram5,
    altTranslationKey: "altInstagram5",
    url: 'https://www.instagram.com/otherthanfood/',
  },
  {
    src: imgInstagram6,
    altTranslationKey: "altInstagram6",
    url: 'https://www.instagram.com/shawnekoh/',
  },
];

const messages = [
  {
    titleTranslationKey: 'goOnFreeAdventures',
    subTranslationKey: 'uncoverHiddenGems',
    altTranslationKey: "altGoOnFreeAdventures",
    image: Banner1Image,
  },
  {
    titleTranslationKey: 'findYourPhotoFam',
    subTranslationKey: 'findYourTribe',
    altTranslationKey: "altFindYourPhotoFam",
    image: Banner2Image,
  },
  {
    titleTranslationKey: 'tellStories',
    subTranslationKey: 'makeADifference',
    altTranslationKey: "altTellStories",
    image: Banner3Image,
  },
]

const CreatorsCommunity = () => {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<Player>();
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/creators-community')

  useEffect(() => {
    const autoPlayVideo = () => {
      const videoContainerEle = videoContainerRef.current;
      const currentPos = window.pageYOffset;
      if (videoContainerEle && player) {
        const loadMorePos = getEleOffset(videoContainerEle).top - HEADER_HEIGHT;
        if (
          currentPos > loadMorePos &&
          currentPos <= loadMorePos + videoContainerEle.offsetHeight
        ) {
          player.play();
        } else {
          player.pause();
        }
      }
    };

    document.addEventListener('scroll', autoPlayVideo);

    return () => {
      document.removeEventListener('scroll', autoPlayVideo);
    };
  }, [player]);

  return (
    <>
      <NextSeo
        title={translation('metaTitle')}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.creatorsCommunityRoute().toUrl())}
        openGraph={{
          url: addLocaleToUrl(AppRoutes.creatorsCommunityRoute().toUrl()),
          title: translation('metaTitle'),
          description: translation('metaDescription'),
        }}
      />
      <WithDefaultJsonLd />

      <div className={styles.heroContainer}>
        <ImageWithFallback
          src={HeroImage}
          alt={translation('altHeroImage')}
          className="tw-object-cover tw-w-full tw-h-full tw-hidden md:tw-block"
        />

        <div className="video tw-block md:tw-hidden">
          <div className={`${videoCardStyle.videoContainer} ${styles.heroVideo} tw-mx-auto`}>
            <VideoCard
              videoId={'j9xFJIoCktA'}
              unmuteOnPlay={true}
              videoConfig={{ autoplay: 1, loop: 1 }}
              hideAllButtons={true}
              noVideoImg={noVideoImg}
            />
          </div>
        </div>

        <div className="tw-absolute tw-bg-black tw-opacity-20 tw-w-full tw-h-full tw-left-0 tw-bottom-0 tw-rounded-lg"></div>
        <div className="tw-absolute tw-top-32 md:tw-top-40 tw-w-full tw-px-6 md:tw-px-10 tw-z-10">
          <h1 className="tw-font-styleSans tw-text-6xl md:tw-text-8xl tw-font-semibold tw-text-white tw-leading-none md:tw-leading-none tw-text-center">
            {translation('adventureAwaits')} <br />
            {translation('joinYourTribe')}
          </h1>
          <div className="tw-text-center tw-mt-8">
            <Link legacyBehavior prefetch={false} href={registrationUrl}>
              <a className="btn btn-primary">{translation('joinTheTribe')}</a>
            </Link>
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24" ref={videoContainerRef}>
        <div className="container">
          <div className="row tw-items-center">
            <div className="col-md-6">
              <div className="md:tw-ml-32">
                <div className="tw-text-center md:tw-text-left">
                  <ImageWithFallback
                    src={IlluImage}
                    alt=""
                    className="tw-static tw-inline-block tw-w-40 md:tw-w-56 tw-h-auto tw-object-cover tw-mb-6"
                  />
                  <h2 className="tw-text-brand-navy tw-font-styleSans tw-font-semibold tw-text-4xl tw-leading-none tw-mb-4">
                    {translation('lookingToTell')}
                    <br />
                    {translation('extraordinary')}
                    <br />
                    {translation('stories')}
                  </h2>
                  <div className="tw-hidden md:tw-block">
                    {translation('joinOurCreator')}
                    <br /> {translation('hiddenPlaces')}
                  </div>
                </div>
              </div>

              <div className="tw-border-t tw-border-grey-lighter tw-mt-16 tw-block md:tw-hidden"></div>
            </div>

            <div className="col-md-6">
              <div className="tw-flex md:tw-flex-col md:tw-items-end">
                <div className="video tw-hidden md:tw-block">
                  <div
                    className={`${videoCardStyle.videoContainer} ${styles.videoCard} tw-mx-auto tw-border-2 tw-border-brand-green`}
                  >
                    <VideoCard
                      videoId={'j9xFJIoCktA'}
                      unmuteOnPlay={true}
                      setPlayer={setPlayer}
                      noVideoImg={noVideoImg}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-mt-16 md:tw-mt-20">
            <Slider
              {...commonCarouselSettings}
              arrows={false}
              dots={true}
              slidesToShow={3}
              slidesToScroll={1}
              className="carousel-homework tw--mx-3.75"
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                  },
                },
              ]}
            >
              {messages.map((message, index) => (
                <div className="tw-px-3.75 tw-flex tw-flex-col" key={index}>
                  <ImageWithFallback
                    src={message.image}
                    alt={translation(message.altTranslationKey)}
                    className="tw-block tw-static tw-h-80 tw-w-full tw-object-cover md:tw-mb-4 tw-border-2 tw-border-solid tw-border-brand-green tw-rounded-lg tw-order-3 md:tw-order-1"
                  />
                  <div className="tw-text-xs md:tw-text-sm tw-uppercase tw-font-semibold tw-mb-4 tw-text-center tw-order-1 md:tw-order-2">
                    {translation(message.subTranslationKey)}
                  </div>
                  <div className="tw-text-2xl md:tw-text-4xl tw-text-center tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-order-2 md:tw-order-3 tw-mb-4 md:tw-mb-0">
                    {translation(message.titleTranslationKey)}
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </div>

      <div className="container tw-py-16 md:tw-py-24">
        <h2 className="tw-text-2xl md:tw-text-4xl tw-mb-6 md:tw-mb-8 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center">
          {translation('weAreLookingFor')}
        </h2>
        <div className="row">
          <div className="col-md-4 tw-text-center tw-mb-8 md:tw-mb-0">
            <ImageWithFallback
              src={Icon1}
              alt=""
              className={`tw-inline-block tw-static tw-h-24 tw-w-auto`}
            />
            <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-mt-4 tw-mb-2">
              {translation('aStoryTeller')}
            </div>
            <div>{translation('lovesTellingStories')}</div>
          </div>
          <div className="col-md-4 tw-text-center tw-mb-8 md:tw-mb-0">
            <ImageWithFallback
              src={Icon2}
              alt=""
              className={`tw-inline-block tw-static tw-h-24 tw-w-auto`}
            />
            <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-mt-4 tw-mb-2">
              {translation('photography')}
            </div>
            <div>
              {translation('experienced')}
            </div>
          </div>
          <div className="col-md-4 tw-text-center">
            <ImageWithFallback
              src={Icon3}
              alt=""
              className={`tw-inline-block tw-static tw-h-24 tw-w-auto`}
            />
            <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl md:tw-leading-none tw-leading-none tw-mt-4 tw-mb-2">
              {translation('outgoingPersonality')}
            </div>
            <div>{translation('comfortable')}</div>
          </div>
        </div>
        <div className="tw-text-center tw-mt-8 md:tw-mt-16">
          <a target="_blank" rel="noreferrer" href={registrationUrl} className="btn btn-primary">
            {translation('joinTheTribe')}
          </a>
        </div>
      </div>

      <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
        <div className="container">
          <h2 className="tw-text-2xl md:tw-text-4xl tw-mb-4 md:tw-mb-6 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center">
            {translation('frequentlyAskedQuestions')}
          </h2>

          <div id="faq" className="tw-max-w-3xl tw-mx-auto">
            {faqs(translation).map((faq, index) => (
              <AccordionFaq key={faq.question} keyName={`faq-${index}`} title={faq.question}>
                {faq.answer}
              </AccordionFaq>
            ))}
          </div>
        </div>
      </div>

      <div className="tw-bg-brand-navy tw-py-16 md:tw-py-24">
        <div className="container">
          <h2 className="tw-font-semibold tw-leading-none tw-text-5xl md:tw-text-6xl tw-font-styleSans tw-text-brand-green tw-mb-8 md:tw-mb-16 tw-text-center">
            {translation('hearItFromOurCreators')}
          </h2>
          <ClientOnlyComponent>
            <CardShuffle />
          </ClientOnlyComponent>
        </div>
      </div>

      <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
        <div className="container">
          <div className="tw-mb-8 tw-text-center">
            <h2 className="tw-text-2xl md:tw-text-4xl tw-mb-0 tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-text-center">
              {translation('meetOurCommunity')}
            </h2>
            <div>{translation('hereAreSome')}</div>
          </div>

          <div className="row">
            {igImages.map((image, index) => (
              <div key={index} className="col-4 tw-mb-4 md:tw-mb-8">
                <Link legacyBehavior prefetch={false} href={image.url}>
                  <a className="tw-block tw-relative" style={{ paddingBottom: '100%' }}>
                    <ImageWithFallback
                      src={image.src}
                      alt={translation(image.altTranslationKey)}
                      className="tw-h-full tw-w-full tw-object-cover tw-border-2 tw-border-solid tw-border-brand-green tw-rounded-lg"
                      fill
                    />
                  </a>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/creators-community']);

  return { props: translations }
};

export default CreatorsCommunity;
